import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Panzoom, { PanzoomObject } from '@panzoom/panzoom';

interface ImageViewerProps {
    src: string;
    alt?: string;
    onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ src, alt, onClose }) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const panzoomRef = useRef<PanzoomObject | null>(null);

    useEffect(() => {
        if (!imgRef.current || !containerRef.current) return;

        const img = imgRef.current;
        const container = containerRef.current;

        const initPanzoom = () => {
            panzoomRef.current = Panzoom(img, {
                maxScale: 8,
                minScale: 0.5,
                startScale: 1,
                cursor: 'move',
            });

            const onWheel = (event: WheelEvent) => {
                panzoomRef.current?.zoomWithWheel(event);
            };

            container.addEventListener('wheel', onWheel);

            return onWheel;
        };

        let onWheelCleanup: ((event: WheelEvent) => void) | undefined;

        if (img.complete) {
            onWheelCleanup = initPanzoom();
        } else {
            img.addEventListener('load', () => {
                onWheelCleanup = initPanzoom();
            });
        }

        return () => {
            if (onWheelCleanup) {
                container.removeEventListener('wheel', onWheelCleanup);
            }
            panzoomRef.current?.destroy();
        };
    }, []);

    // Handle Escape key and body styling
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalBackground = document.body.style.backgroundColor;

        document.body.style.overflow = 'hidden';
        // We set background to black to ensure safe areas are black if overlay doesn't reach
        document.body.style.backgroundColor = 'black';

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = originalOverflow;
            document.body.style.backgroundColor = originalBackground;
        };
    }, [onClose]);

    const handleZoomIn = () => panzoomRef.current?.zoomIn();
    const handleZoomOut = () => panzoomRef.current?.zoomOut();
    const handleReset = () => panzoomRef.current?.reset();

    // Stop propagation on controls to prevent closing when clicking buttons if we add backdrop click close
    const handleControlClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black animate-in fade-in duration-200"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, scrollbarWidth: 'none' }}
            onClick={onClose} // Click background to close
        >
            {/* Close Button - Top Right */}
            <button
                onClick={onClose}
                className="absolute z-[10000] p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-200 backdrop-blur-md border border-white/10 group"
                style={{
                    top: 'calc(1rem + env(safe-area-inset-top, 0px))',
                    right: 'calc(1rem + env(safe-area-inset-right, 0px))'
                }}
                aria-label="Close"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>

            <div className="absolute inset-0 flex items-center justify-center p-4">
                {/* Main Image Container */}
                <div
                    ref={containerRef}
                    className="w-full h-full flex items-center justify-center overflow-hidden"
                >
                    <img
                        ref={imgRef}
                        src={src}
                        alt={alt || "Zoomable content"}
                        className="block mx-auto max-w-full max-h-full object-contain shadow-2xl"
                        style={{ margin: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>

            {/* Bottom Controls */}
            <div
                className="absolute left-1/2 -translate-x-1/2 z-[110] flex items-center gap-2 p-2 rounded-2xl bg-black/60 border border-white/20 backdrop-blur-xl shadow-2xl"
                style={{
                    bottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'
                }}
                onClick={handleControlClick}
            >
                <button
                    onClick={handleZoomOut}
                    className="p-3 text-white hover:bg-white/20 rounded-xl transition-colors active:scale-95"
                    title="Zoom Out"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                </button>

                <button
                    onClick={handleReset}
                    className="p-3 text-white hover:bg-white/20 rounded-xl transition-colors active:scale-95 border-x border-white/10 mx-1"
                    title="Reset"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                        <path d="M3 3v5h5"></path>
                    </svg>
                </button>

                <button
                    onClick={handleZoomIn}
                    className="p-3 text-white hover:bg-white/20 rounded-xl transition-colors active:scale-95"
                    title="Zoom In"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="11" y1="8" x2="11" y2="14"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                </button>
            </div>

            <div
                className="absolute right-8 text-white/40 text-xs hidden md:block pointer-events-none"
                style={{
                    bottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'
                }}
            >
                Scroll to zoom • Drag to pan
            </div>
        </div>,
        document.body
    );
};

export default ImageViewer;
